import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import { Link } from 'react-router-dom'

import { StaffInList } from '../../core/_models'
import { ROLE_NAME } from '../../../../../constants'
import { KTIcon } from '../../../../../_metronic/helpers'
import { postBlockEmployee } from '../../api/postBlockEmployee'
import { postUnblockEmployee } from '../../api'
import { postResetSecondFactor } from '../../api/secondFactor'
import { StaffSecondFactor } from '../../core/useSecondFactorUserIds'
import { ROLE, useAuth } from '../../../auth'
import { IsGranted } from '../../../auth/PrivateRoute'

const AppSwal = withReactContent(Swal)

export const StaffItem = ({
  staff,
  active,
  callback,
  showActions,
  secondFactor,
}: {
  staff: StaffInList
  active: boolean
  callback?: () => void
  showActions?: boolean
  /** The account's second factor; the reset is offered only for a protected account. */
  secondFactor?: StaffSecondFactor
}) => {
  const { currentUser } = useAuth()

  if (staff.roles) {
    const removeRoleIndex = staff.roles.indexOf('ROLE_USER')

    if (removeRoleIndex !== -1) {
      staff.roles.splice(removeRoleIndex, 1)
    }
  }

  const onClickUnblockAction = () => {
    AppSwal.fire({
      title: `Вы точно хотите разблокировать сотрудника "${staff.uuid}"?`,
      showCancelButton: true,
      cancelButtonText: 'Нет',
      confirmButtonText: 'Да, разблокировать',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const response = await postUnblockEmployee(staff.id)

          if (response.data && response.data.success) {
          } else {
            throw new Error(response.data.errors.general_error)
          }
        } catch (err) {
          AppSwal.showValidationMessage(`Неверный запрос: ${err}`)
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed) {
        AppSwal.fire({
          title: `Сотрудник "${staff.uuid}" успешно разблокирован!`,
          timer: 1500,
        })

        callback && callback()
      }
    })
  }

  const onClickBlockAction = () => {
    AppSwal.fire({
      title: `Вы точно хотите заблокировать сотрудника "${staff.uuid}"?`,
      showCancelButton: true,
      cancelButtonText: 'Нет',
      confirmButtonText: 'Да, заблокировать',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const response = await postBlockEmployee(staff.id)

          if (response.status === 400) {
            Swal.fire('', `Невозможно заблокировать сотрудника "${staff.uuid}"`, 'warning')
          }

          if (response.data && response.data.success) {
          } else {
            throw new Error(response.data.errors.general_error)
          }
        } catch (err) {
          AppSwal.showValidationMessage(`Неверный запрос: ${err}`)
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed) {
        AppSwal.fire({
          title: `Сотрудник "${staff.uuid}" успешно заблокирован!`,
          timer: 1500,
        })

        callback && callback()
      }
    })
  }

  const onClickResetSecondFactor = () => {
    AppSwal.fire({
      title: `Снять второй фактор у сотрудника "${staff.uuid}"?`,
      text: 'Сотрудник войдёт по паролю и подключит второй фактор заново. Прежние коды и ключи перестанут работать.',
      showCancelButton: true,
      cancelButtonText: 'Нет',
      confirmButtonText: 'Да, снять',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const response = await postResetSecondFactor(staff.id)
          if (!response.data?.success) {
            throw new Error('refused')
          }
        } catch {
          Swal.showValidationMessage('Не удалось снять второй фактор.')

          // keeps the dialog open explicitly rather than by the message being visible
          return false
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed) {
        AppSwal.fire({ title: `Второй фактор сотрудника "${staff.uuid}" снят`, timer: 1500 })

        callback && callback()
      }
    })
  }

  return (
    <tr className={active ? '' : 'table-danger'}>
      <td className='fw-bold'>{staff.id}</td>
      <td>{staff.uuid}</td>
      <td>
        {staff.roles &&
          staff.roles
            .reduce((arr: string[], r: string) => {
              if (Object.keys(ROLE_NAME).includes(r)) {
                arr.push(ROLE_NAME[r])
              }

              return arr
            }, [])
            .sort()
            .join(', ')}
      </td>
      {showActions && IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER], currentUser) && (
        <td data-testid='staff-second-factor'>
          {secondFactor === undefined && <span className='text-muted'>—</span>}
          {secondFactor === null && <span className='text-muted'>Не включён</span>}
          {secondFactor && (
            <span className='d-inline-flex align-items-center gap-2'>
              <span className='badge badge-light-success'>
                {secondFactor.passkeys > 0 ? `Включён, ключей: ${secondFactor.passkeys}` : 'Включён'}
              </span>
              <button
                type='button'
                className='btn btn-icon btn-sm btn-active-light-danger w-25px h-25px'
                onClick={onClickResetSecondFactor}
                title='Снять второй фактор'
                aria-label='Снять второй фактор'
              >
                <KTIcon className='fs-2' iconName='shield-cross' />
              </button>
            </span>
          )}
        </td>
      )}
      {showActions && IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER], currentUser) && (
        <td>
          <Link
            to={`/staff/profile/${staff.uuid}`}
            className='cursor-pointer d-inline-block me-3'
            title='Профиль'
          >
            <KTIcon className='fs-1' iconName='profile-circle' />
          </Link>
          {active ? (
            <div className='cursor-pointer' onClick={onClickBlockAction} title='Заблокировать'>
              <KTIcon className='fs-1' iconName='cross' />
            </div>
          ) : (
            <div className='cursor-pointer' onClick={onClickUnblockAction} title='Разблокировать'>
              <KTIcon className='fs-1' iconName='check' />
            </div>
          )}
        </td>
      )}
    </tr>
  )
}
